import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addLocation } from "../slices/location-slice";
import { googleMapsApiKey } from "../env/environment";
import { useJsApiLoader } from "@react-google-maps/api";
import { extractCity } from "../utils";

const GOOGLE_MAPS_API_KEY = googleMapsApiKey;

const useUserLocation = () => {
    const autocompleteRef = useRef(null);
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [coords, setCoords] = useState(null);
    const [error, setError] = useState("");
    useJsApiLoader({ googleMapsApiKey: googleMapsApiKey, libraries: ["places", "marker"] });
    const dispatch = useDispatch();
    const storedLocation = useSelector((state) => state.location);

    // Handle autocomplete selection
    const handlePlaceSelect = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place && place.geometry) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const cityName = extractCity(place.address_components);
                setCity(cityName);
                setAddress(place.formatted_address);
                setCoords({ lat, lng });
                dispatch(addLocation({
                    address: place.formatted_address,
                    coords: { lat, lng },
                    city: cityName,
                }));
            }
        }
    };

    useEffect(() => {

        if (storedLocation?.coords?.lat) return;

        if (!navigator.geolocation) {
            setError("Geolocation is not supported by this browser.");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                try {
                    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`);
                    const data = await response.json();

                    if (data.status === "OK") {
                        const addressComponents = data.results[0]?.address_components;
                        const cityName = extractCity(addressComponents);
                        const locationData = {
                            address: data.results[0]?.formatted_address || "Address not found",
                            coords: { lat: latitude, lng: longitude },
                            city: cityName
                        };

                        dispatch(addLocation(locationData));
                    } else {
                        setError("Error fetching address.");
                    }
                } catch {
                    setError("Failed to fetch address.");
                }
            },
            (err) => {
                const errorMessages = {
                    1: "Location access denied. Please enable location permissions.",
                    2: "Location unavailable.",
                    3: "Location request timed out. Try again.",
                };
                setError(errorMessages[err.code] || "An unknown error occurred.");
            }
        );
    }, [dispatch, storedLocation]);

    return {
        location: storedLocation,
        error,
        address,
        coords,
        autocompleteRef,
        handlePlaceSelect,
        setAddress
    };
};

export default useUserLocation;
